import { LISTING_STATE_PUBLISHED, EXTENDED_DATA_SCHEMA_TYPES } from '../../util/types';
import { isFieldForListingType } from '../../util/fieldHelpers';
import { isBookingProcessAlias, isPurchaseProcessAlias } from '../../transactions/transaction';

const PAIRED_LISTING_ID_KEY = 'pairedListingId';

/**
 * Find the listing type config that represents the "opposite" transaction process
 * of the given source listing type: booking <-> purchase. Returns null if the source
 * type isn't booking/purchase, or no complementary type is configured for this marketplace.
 *
 * @param {String} sourceListingType - listingType id of the source listing (e.g. 'daily-booking')
 * @param {Array<Object>} listingTypes - config.listing.listingTypes
 * @returns {Object|null} the complementary listing type config, or null
 */
export const findComplementaryListingType = (sourceListingType, listingTypes = []) => {
  const sourceConfig = listingTypes.find(lt => lt.listingType === sourceListingType);
  const sourceAlias = sourceConfig?.transactionType?.alias;

  const isSourceBooking = isBookingProcessAlias(sourceAlias);
  const isSourcePurchase = isPurchaseProcessAlias(sourceAlias);

  if (!isSourceBooking && !isSourcePurchase) {
    return null;
  }

  const targetConfig = listingTypes.find(lt => {
    const alias = lt?.transactionType?.alias;
    return isSourceBooking ? isPurchaseProcessAlias(alias) : isBookingProcessAlias(alias);
  });

  return targetConfig || null;
};

/**
 * Whether a "Add a Buy/Rent version" action should be offered for the given listing.
 *
 * @param {Object} listing - own listing entity
 * @param {Object} config - marketplace configuration (needs config.listing.listingTypes)
 * @returns {{ eligible: boolean, targetListingTypeConfig: Object|null }}
 */
export const isPairingEligible = (listing, config) => {
  const { state, publicData } = listing?.attributes || {};
  const isPublished = state === LISTING_STATE_PUBLISHED;
  const alreadyPaired = !!publicData?.[PAIRED_LISTING_ID_KEY];

  if (!isPublished || alreadyPaired) {
    return { eligible: false, targetListingTypeConfig: null };
  }

  const targetListingTypeConfig = findComplementaryListingType(
    publicData?.listingType,
    config?.listing?.listingTypes
  );

  return { eligible: !!targetListingTypeConfig, targetListingTypeConfig };
};

/**
 * Build the `data` payload for `requestCreateListingDraft`, copying over the parts of
 * the source listing that make sense for the target (complementary) listing type:
 * title, description, images, category, and any custom fields configured for BOTH
 * listing types. Pricing, location/availability, and delivery are intentionally left
 * out since they differ structurally between rent and buy listings.
 *
 * @param {Object} sourceListing - full listing entity (with all images included)
 * @param {Object} targetListingTypeConfig - the complementary listing type config
 * @param {Array<Object>} listingFieldConfigs - config.listing.listingFields
 * @param {Array<Object>} images - fresh image entities to attach to the new listing
 *   (Sharetribe doesn't allow reusing image IDs already attached to another listing,
 *   so these must already be newly-uploaded copies, not sourceListing.images directly)
 * @returns {Object} data payload for requestCreateListingDraft
 */
export const computePairedListingData = (
  sourceListing,
  targetListingTypeConfig,
  listingFieldConfigs = [],
  images = []
) => {
  const { title, description, publicData = {}, privateData = {} } = sourceListing.attributes;
  const sourceListingType = publicData.listingType;
  const targetListingType = targetListingTypeConfig.listingType;

  const categoryEntries = Object.entries(publicData).filter(([key]) =>
    key.startsWith('categoryLevel')
  );

  const sharedFieldConfigs = listingFieldConfigs.filter(fieldConfig => {
    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(fieldConfig.schemaType);
    return (
      isKnownSchemaType &&
      fieldConfig.key !== PAIRED_LISTING_ID_KEY &&
      isFieldForListingType(sourceListingType, fieldConfig) &&
      isFieldForListingType(targetListingType, fieldConfig)
    );
  });

  const customFieldEntries = sharedFieldConfigs.reduce((entries, fieldConfig) => {
    const { key, scope = 'public' } = fieldConfig;
    const sourceData = scope === 'private' ? privateData : publicData;
    const value = sourceData?.[key];
    return value != null ? [...entries, [key, value]] : entries;
  }, []);

  const newPublicData = {
    listingType: targetListingType,
    transactionProcessAlias: targetListingTypeConfig.transactionType.alias,
    unitType: targetListingTypeConfig.transactionType.unitType,
    ...Object.fromEntries(categoryEntries),
    ...Object.fromEntries(customFieldEntries),
    [PAIRED_LISTING_ID_KEY]: sourceListing.id.uuid,
  };

  return {
    title,
    description,
    images,
    publicData: newPublicData,
  };
};
